import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import Logo from "@/components/Logo";
import { Building2, User, FileText, CheckCircle2 } from "lucide-react";

const STEPS = ["Compte", "Représentant", "Agence", "Confirmation"];

export default function RegisterAgency() {
  const navigate = useNavigate();
  const { setUser } = useAuth() || {};
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "",
    representative_first_name: "", representative_last_name: "", phone: "",
    agency_name: "", country: "", city: "", address: "",
    rccm_number: "", legal_form: "", year_founded: "", vehicles_count: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canProceed = () => {
    if (step === 0) return form.email && form.password.length >= 6;
    if (step === 1) return form.representative_first_name && form.representative_last_name && form.phone;
    if (step === 2) return form.agency_name && form.country && form.city;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        year_founded: form.year_founded ? parseInt(form.year_founded) : null,
        vehicles_count: form.vehicles_count ? parseInt(form.vehicles_count) : null,
      };
      const { data } = await api.post("/auth/register-agency", payload);
      localStorage.setItem("mbk_token", data.access_token);
      if (setUser) setUser(data.user);
      toast.success("Demande d'inscription envoyée — bienvenue chez MOBIKAR");
      setStep(3);
    } catch (e) {
      toast.error(extractError(e, "Erreur lors de l'inscription"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a1e3f] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8"><Logo variant="light" size="lg" /></div>
        <div className="bg-white rounded-3xl p-6 md:p-10 mbk-card-shadow" data-testid="register-agency-page">
          <div className="mb-6">
            <div className="flex justify-between text-xs font-semibold text-gray-500 mb-2">
              {STEPS.map((s, i) => (<span key={s} className={i <= step ? "text-[#0e7a3f]" : ""}>{i + 1}. {s}</span>))}
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-2 bg-gray-100" />
          </div>

          {step === 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1"><User className="h-5 w-5 text-[#0e7a3f]" /><h1 className="font-display text-2xl font-black text-[#0a1e3f]">Créer le compte</h1></div>
              <p className="text-sm text-gray-500 mb-6">Cet email servira à vous connecter à votre espace agence.</p>
              <div className="space-y-4">
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Adresse email professionnelle</Label><Input data-testid="ra-email" type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} className="mt-2 h-12" /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Mot de passe (6 caractères min.)</Label><Input data-testid="ra-password" type="password" required minLength={6} value={form.password} onChange={(e) => upd("password", e.target.value)} className="mt-2 h-12" /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="flex items-center gap-2 mb-1"><User className="h-5 w-5 text-[#0e7a3f]" /><h1 className="font-display text-2xl font-black text-[#0a1e3f]">Représentant légal</h1></div>
              <p className="text-sm text-gray-500 mb-6">Personne qui signera le contrat de partenariat au nom de l'agence.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Prénom</Label><Input data-testid="ra-first" value={form.representative_first_name} onChange={(e) => upd("representative_first_name", e.target.value)} className="mt-2 h-12" /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Nom</Label><Input data-testid="ra-last" value={form.representative_last_name} onChange={(e) => upd("representative_last_name", e.target.value)} className="mt-2 h-12" /></div>
                <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Téléphone</Label><Input data-testid="ra-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="mt-2 h-12" placeholder="+242..." /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex items-center gap-2 mb-1"><Building2 className="h-5 w-5 text-[#0e7a3f]" /><h1 className="font-display text-2xl font-black text-[#0a1e3f]">Informations agence</h1></div>
              <p className="text-sm text-gray-500 mb-6">Renseignez les informations légales de votre agence. Les documents seront téléversés depuis votre espace après connexion.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Nom de l'agence</Label><Input data-testid="ra-agency-name" value={form.agency_name} onChange={(e) => upd("agency_name", e.target.value)} className="mt-2 h-12" /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Pays</Label><Input data-testid="ra-country" value={form.country} onChange={(e) => upd("country", e.target.value)} className="mt-2 h-12" placeholder="Congo" /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Ville</Label><Input data-testid="ra-city" value={form.city} onChange={(e) => upd("city", e.target.value)} className="mt-2 h-12" placeholder="Pointe-Noire" /></div>
                <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Adresse</Label><Input data-testid="ra-address" value={form.address} onChange={(e) => upd("address", e.target.value)} className="mt-2 h-12" /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">N° RCCM</Label><Input data-testid="ra-rccm" value={form.rccm_number} onChange={(e) => upd("rccm_number", e.target.value)} className="mt-2 h-12" placeholder="CG/PNR/..." /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Forme juridique</Label><Input data-testid="ra-legal" value={form.legal_form} onChange={(e) => upd("legal_form", e.target.value)} className="mt-2 h-12" placeholder="SARL / SA / EI..." /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Année de création</Label><Input data-testid="ra-year" type="number" value={form.year_founded} onChange={(e) => upd("year_founded", e.target.value)} className="mt-2 h-12" placeholder="2020" /></div>
                <div><Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Nombre de véhicules</Label><Input data-testid="ra-vcount" type="number" value={form.vehicles_count} onChange={(e) => upd("vehicles_count", e.target.value)} className="mt-2 h-12" placeholder="5" /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-4">
              <div className="h-16 w-16 rounded-full bg-[#dcfce7] text-[#0e7a3f] flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8" /></div>
              <h2 className="font-display text-2xl font-black text-[#0a1e3f] mt-4">Demande envoyée !</h2>
              <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">Votre dossier est <b>en attente de validation MOBIKAR</b>. Vous pouvez maintenant vous connecter et téléverser vos documents (pièce d'identité, RCCM, justificatif d'activité).</p>
              <div className="mt-6 flex gap-3 justify-center">
                <Button data-testid="ra-goto-dashboard" onClick={() => navigate("/admin/dossier")} className="mbk-btn-green text-white h-11 px-6 rounded-full">Aller à mon dossier</Button>
              </div>
            </div>
          )}

          {step < 3 && (
            <div className="mt-8 flex items-center justify-between gap-3">
              <Button variant="outline" disabled={step === 0 || busy} onClick={() => setStep((s) => Math.max(0, s - 1))} className="rounded-full h-11 px-5">Précédent</Button>
              {step < 2 ? (
                <Button data-testid="ra-next" disabled={!canProceed() || busy} onClick={() => setStep((s) => s + 1)} className="mbk-btn-primary text-white h-11 px-6 rounded-full">Continuer</Button>
              ) : (
                <Button data-testid="ra-submit" disabled={!canProceed() || busy} onClick={submit} className="mbk-btn-green text-white h-11 px-6 rounded-full font-semibold">{busy ? "Envoi…" : "Créer mon agence"}</Button>
              )}
            </div>
          )}

          {step < 3 && (
            <div className="text-center mt-6 text-sm text-gray-500">
              Déjà un compte ? <Link to="/login" className="text-[#0a1e3f] font-bold hover:underline">Se connecter</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
